import { IFormFieldModel } from 'app/overview/config/configuration_tab/general/frontend_config/IFormFieldModel';
import {
    AVAILABLE_IMS_TERRAIN_PROVIDER_TYPES,
    ImsTerrainProvider,
    ImsTerrainProviderType,
} from 'app/config/ImsTerrainProvider';
import { EventRegistry } from 'app/shared/events/event_registry';
import { IMapLayerProviderFormModel } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderFormModel';
import { IMapLayerProviderPersister } from 'app/overview/config/configuration_tab/general/frontend_config/common/IMapLayerProviderPersister';
import {
    THIS_FIELD_IS_REQUIRED,
    THIS_VALUE_IS_TOO_HIGH,
    THIS_VALUE_IS_TOO_LOW,
} from 'app/overview/config/configuration_tab/general/frontend_config/common/layer_provider_constants';
import { ConfigService } from 'app/config/config.service';
import { isValidRequiredString } from 'app/form_config/components/string_input';
import { isValidNumber } from 'app/form_config/components/numeric_input';
import { TerrainService } from 'app/overview/config/configuration_tab/general/frontend_config/terrain/terrain.service';

interface AvailableTerrainProviderType {
    name: string;
    displayName: string;
}

interface TerrainProviderFormViewState {
    // Values and messages
    name: string;
    nameErrMsg: string;
    cesiumIonId: string;
    cesiumIonIdErrMsg: string;
    terrainDetailLevel: string; // This should be a string representation of a number
    terrainDetailLevelErrMsg: string;
    terrainDetailLevelWarningMsg: string;
    terrainLayerCreationDate: string;
    availableTypes: AvailableTerrainProviderType[];
    type: string;
    url: string;
    urlErrMsg: string;
    relativeUrl: boolean;

    // Is the form in an expanded state
    expanded: boolean;

    // For controlling dynamic fields
    showUrlInput: boolean;
    showCesiumIonIdInput: boolean;

    // For handling the save button
    isDirty: boolean;
    isValid: boolean;
}

/**
 * Give this to the component. It's a mix of controller / model.
 */
export class TerrainProviderFormModel
    implements
        IFormFieldModel<TerrainProviderFormViewState>,
        IMapLayerProviderFormModel<ImsTerrainProvider>
{
    private _persister: IMapLayerProviderPersister;
    private _uuid: string;

    private _onChange: EventRegistry;

    // Is the form expanded
    private _expanded: boolean;

    private _origValue: ImsTerrainProvider;
    private _valueUnderEdit: ImsTerrainProvider;
    private terrainService: TerrainService;

    /**
     * View state calculated by list form model
     *
     * @param {IMapLayerProviderPersister} persister
     * @param origValue - don't edit it in this class
     * @param valueUnderEdit - don't edit it in this class
     */
    constructor(
        persister: IMapLayerProviderPersister,
        origValue: ImsTerrainProvider,
        valueUnderEdit: ImsTerrainProvider,
    ) {
        this._uuid = valueUnderEdit.uuid;

        this._onChange = new EventRegistry();

        if (!this._uuid) {
            throw new Error(`No uuid for terrain provider under editing!`);
        }

        this._expanded = false;
        this._persister = persister;

        this._origValue = origValue;
        this._valueUnderEdit = valueUnderEdit.clone();

        this.terrainService = new TerrainService();
    }

    get valueUnderEdit(): ImsTerrainProvider {
        return this._valueUnderEdit;
    }

    /**
     *
     * @param callback callback func
     * @returns {() => void} destroy listener anon func
     */
    onChange(callback): () => void {
        const result = this._onChange.addEventListener(callback);
        this._notifyListeners();
        return result;
    }

    get uuid(): string {
        return this._valueUnderEdit.uuid;
    }

    /**
     * Called from parent form
     * Inform that this object that the original value has changed
     *
     * Would be nicer as a listener
     *
     * @param {ImsTerrainProvider} provider
     */
    updateOrigValue(provider: ImsTerrainProvider): void {
        if (!this.isDirty()) {
            // There are no changes
            this._origValue = provider;
            this.revert();
        } else {
            // There are outstanding changes and there are new changes
            // coming in from the API
            this._origValue = provider;
        }
    }

    /**
     * Used by UI component
     *
     * Expanded is state local to the form. The list form does not need
     * to be informed of the expanded state.
     *
     * @param {boolean} expanded
     */
    setExpanded(expanded: boolean) {
        this._expanded = expanded;
        this.updateTerrainLayerCreationDate();
        this._notifyListeners();
    }

    /**
     * Used by UI component
     * @param {string} name
     */
    setName(name: string) {
        this._valueUnderEdit.name = name;
        this._notifyListeners();
    }

    /**
     * Used by UI component
     * @param {string} type
     */
    setType(type: string) {
        this._valueUnderEdit.type = type;
        this._notifyListeners();
    }

    /**
     * Used by UI component
     * @param {string} level
     */
    setTerrainDetailLevel(level: number) {
        this._valueUnderEdit.terrainDetailLevel = isValidNumber(level) ? level : null;
        this._notifyListeners();
    }

    /**
     * Used by UI component
     * @param {string} url
     */
    setUrl(url: string) {
        this._valueUnderEdit.url = url;
        this.updateTerrainLayerCreationDate();
        this._notifyListeners();
    }

    /**
     * Used by UI component
     * @param {boolean} value
     */
    setRelativeUrl(value: boolean) {
        this._valueUnderEdit.relativeUrl = value;
        this.updateTerrainLayerCreationDate();
        this._notifyListeners();
    }

    /**
     * Used by UI component
     * @param {number} value
     */
    setCesiumIonId(value: number) {
        if (value < 1) {
            value = null;
        }
        this._valueUnderEdit.cesiumIonId = value;
        this._notifyListeners();
    }

    destroy(): void {}

    getId(): string {
        return this._uuid;
    }

    getViewState(): TerrainProviderFormViewState {
        const isDirty = this._origValue && this._origValue.isDiff(this._valueUnderEdit);

        const nameErrMsg = !isValidRequiredString(this._valueUnderEdit.name)
            ? THIS_FIELD_IS_REQUIRED
            : null;
        const terrainDetailLevelErrMsg = !isValidNumber(
            this._valueUnderEdit.terrainDetailLevel,
            true,
        )
            ? THIS_FIELD_IS_REQUIRED
            : null;

        let terrainDetailLevelWarningMsg = null;
        if (this._valueUnderEdit.terrainDetailLevel > ConfigService.terrainDetailHighWarningLevel) {
            terrainDetailLevelWarningMsg = THIS_VALUE_IS_TOO_HIGH;
        } else if (
            this._valueUnderEdit.terrainDetailLevel < ConfigService.terrainDetailLowWarningLevel
        ) {
            terrainDetailLevelWarningMsg = THIS_VALUE_IS_TOO_LOW;
        }

        const isCustom = this._valueUnderEdit.type === ImsTerrainProviderType.CUSTOM;

        let allErrors = [];

        let urlErrMsg = null;
        if (isCustom) {
            urlErrMsg = !isValidRequiredString(this._valueUnderEdit.url)
                ? THIS_FIELD_IS_REQUIRED
                : null;
            allErrors = [nameErrMsg, terrainDetailLevelErrMsg, urlErrMsg];
        }

        const isIon = this._valueUnderEdit.type === ImsTerrainProviderType.ION;
        let cesiumIonIdErrMsg = null;
        if (isIon) {
            cesiumIonIdErrMsg = !isValidNumber(this._valueUnderEdit.cesiumIonId, true)
                ? THIS_FIELD_IS_REQUIRED
                : null;
            allErrors = [nameErrMsg, terrainDetailLevelErrMsg, cesiumIonIdErrMsg];
        }

        return {
            name: this._valueUnderEdit.name,
            nameErrMsg,
            cesiumIonId: this._valueUnderEdit.cesiumIonId
                ? this._valueUnderEdit.cesiumIonId.toString()
                : null,
            cesiumIonIdErrMsg,
            terrainDetailLevel: this._valueUnderEdit.terrainDetailLevel
                ? this._valueUnderEdit.terrainDetailLevel.toString()
                : '',
            terrainDetailLevelErrMsg,
            terrainDetailLevelWarningMsg,
            terrainLayerCreationDate: this._valueUnderEdit.terrainLayerCreationDate,
            availableTypes: AVAILABLE_IMS_TERRAIN_PROVIDER_TYPES,
            type: this._valueUnderEdit.type,
            url: this._valueUnderEdit.url,
            urlErrMsg,
            relativeUrl: this._valueUnderEdit.relativeUrl,

            showUrlInput: isCustom,
            showCesiumIonIdInput: isIon,

            expanded: this._expanded,

            isDirty,
            // If there are any error messages, the form is not valid
            isValid: !allErrors.some((v) => v !== null),
        };
    }

    isDirty(): boolean {
        return this.getViewState().isDirty;
    }

    isValid(): boolean {
        return this.getViewState().isValid;
    }

    revert(): void {
        if (!this._origValue) {
            throw new Error('not handled - should never get here');
        }
        this._valueUnderEdit = this._origValue.clone();
        this._notifyListeners();
    }

    delete(): void {
        this._persister.deleteProviderForm(this.uuid);
    }

    save(): Promise<any> {
        throw new Error('not implemented');
    }

    protected _notifyListeners() {
        this._onChange.notifyListeners(this.getViewState());
        // notify upstream form handler that this lower level item has changed
        this._persister.providerFormUpdated();
    }

    updateTerrainLayerCreationDate(): void {
        if (this._valueUnderEdit.relativeUrl) {
            this.terrainService
                .getTerrainLayerCreationDate(this._valueUnderEdit.url)
                .then((response) => {
                    this._valueUnderEdit.terrainLayerCreationDate = response;
                    this._notifyListeners();
                });
        }
    }
}
